import React, { useState } from 'react';
import { GlobeIcon, BuildingIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
interface COASegment {
  id: string;
  segmentCode: string;
  segmentName: string;
  fullGLPath: string;
  scopeType?: 'global' | 'specific';
  applicableOrgUnitIds?: string[];
}
interface OrgUnit {
  id: string;
  code: string;
  name: string;
  path: string;
}
interface COAScopeViewerProps {
  coaSegments: COASegment[];
  orgUnits: OrgUnit[];
}
export function COAScopeViewer({
  coaSegments,
  orgUnits
}: COAScopeViewerProps) {
  const [selectedOrgUnitId, setSelectedOrgUnitId] = useState<string>(orgUnits.length > 0 ? orgUnits[0].id : '');
  // Get segments available for the selected OrgUnit
  const availableSegments = coaSegments.filter(segment => {
    if (segment.scopeType === 'global' || !segment.scopeType) {
      return true;
    }
    return segment.applicableOrgUnitIds?.includes(selectedOrgUnitId);
  });
  const unavailableSegments = coaSegments.filter(segment => {
    if (segment.scopeType === 'global' || !segment.scopeType) {
      return false;
    }
    return !segment.applicableOrgUnitIds?.includes(selectedOrgUnitId);
  });
  const selectedOrgUnit = orgUnits.find(ou => ou.id === selectedOrgUnitId);
  return <div className="bg-white shadow rounded-lg p-6" data-id="element-7494">
      <div className="mb-6" data-id="element-7495">
        <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-7496">
          GL Code Availability by Organisation Unit
        </h3>
        <p className="text-sm text-gray-500" data-id="element-7497">
          View which GL codes are available for each organisation unit based on
          scope configuration
        </p>
      </div>

      {/* OrgUnit Selector */}
      <div className="mb-6" data-id="element-7498">
        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7499">
          Select Organisation Unit
        </label>
        <select value={selectedOrgUnitId} onChange={e => setSelectedOrgUnitId(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-7500">
          {orgUnits.map(orgUnit => <option key={orgUnit.id} value={orgUnit.id} data-id="element-7501">
              {orgUnit.name} ({orgUnit.path})
            </option>)}
        </select>
      </div>

      {selectedOrgUnit && <div className="space-y-6" data-id="element-7502">
          {/* Available Segments */}
          <div data-id="element-7503">
            <div className="flex items-center mb-3" data-id="element-7504">
              <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" data-id="element-7505" />
              <h4 className="text-sm font-medium text-gray-900" data-id="element-7506">
                Available GL Codes ({availableSegments.length})
              </h4>
            </div>
            <div className="space-y-2" data-id="element-7507">
              {availableSegments.length === 0 ? <p className="text-sm text-gray-500 italic" data-id="element-7508">
                  No GL codes available for this organisation unit
                </p> : availableSegments.map(segment => <div key={segment.id} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md" data-id="element-7509">
                    <div className="flex-1" data-id="element-7510">
                      <div className="flex items-center" data-id="element-7511">
                        <span className="text-sm font-mono font-bold text-green-900" data-id="element-7512">
                          {segment.fullGLPath}
                        </span>
                        <span className="ml-2 text-sm text-green-700" data-id="element-7513">
                          {segment.segmentName}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4" data-id="element-7514">
                      {segment.scopeType === 'global' || !segment.scopeType ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-7515">
                          <GlobeIcon className="h-3 w-3 mr-1" data-id="element-7516" />
                          Global
                        </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800" data-id="element-7517">
                          <BuildingIcon className="h-3 w-3 mr-1" data-id="element-7518" />
                          Specific
                        </span>}
                    </div>
                  </div>)}
            </div>
          </div>

          {/* Unavailable Segments */}
          {unavailableSegments.length > 0 && <div data-id="element-7519">
              <div className="flex items-center mb-3" data-id="element-7520">
                <XCircleIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-7521" />
                <h4 className="text-sm font-medium text-gray-900" data-id="element-7522">
                  Not Available ({unavailableSegments.length})
                </h4>
              </div>
              <div className="space-y-2" data-id="element-7523">
                {unavailableSegments.map(segment => <div key={segment.id} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md opacity-60" data-id="element-7524">
                    <div className="flex-1" data-id="element-7525">
                      <div className="flex items-center" data-id="element-7526">
                        <span className="text-sm font-mono font-bold text-gray-600" data-id="element-7527">
                          {segment.fullGLPath}
                        </span>
                        <span className="ml-2 text-sm text-gray-500" data-id="element-7528">
                          {segment.segmentName}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4" data-id="element-7529">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700" data-id="element-7530">
                        <BuildingIcon className="h-3 w-3 mr-1" data-id="element-7531" />
                        Restricted
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>}
        </div>}

      {/* Summary Stats */}
      <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-7532">
        <div className="grid grid-cols-3 gap-4" data-id="element-7533">
          <div className="text-center" data-id="element-7534">
            <div className="text-2xl font-bold text-gray-900" data-id="element-7535">
              {coaSegments.length}
            </div>
            <div className="text-xs text-gray-500" data-id="element-7536">Total GL Codes</div>
          </div>
          <div className="text-center" data-id="element-7537">
            <div className="text-2xl font-bold text-green-600" data-id="element-7538">
              {availableSegments.length}
            </div>
            <div className="text-xs text-gray-500" data-id="element-7539">Available</div>
          </div>
          <div className="text-center" data-id="element-7540">
            <div className="text-2xl font-bold text-gray-400" data-id="element-7541">
              {unavailableSegments.length}
            </div>
            <div className="text-xs text-gray-500" data-id="element-7542">Restricted</div>
          </div>
        </div>
      </div>
    </div>;
}